<template>
  <el-dialog
    title="GLB连接点标定"
    :visible.sync="dialogVisible"
    width="1040px"
    append-to-body
    class="glb-anchor-dialog"
    @closed="handleClosed"
  >
    <div class="calibration_wrap" v-loading="loading">
      <div class="preview_panel">
        <div ref="preview" class="preview_canvas"></div>
        <div class="preview_tip">
          红点是根茎连接点，蓝点是花头中心点，绿点是根茎进入方向。选择任一点后，可直接点击模型表面吸附标注点。
        </div>
      </div>
      <div class="config_panel">
        <div class="material_name">{{ resourceName || "GLB花材" }}</div>
        <div class="desc">
          三点标定只影响 GLB 花材：红点负责接上，蓝点负责花头中心，绿点负责根茎进入方向。完成后会输出 modelConfigJson 供前台编辑器直接使用。
        </div>
        <div class="view_group">
          <div class="group_title">视角</div>
          <el-button size="mini" @click="setPreviewView('front')">正视</el-button>
          <el-button size="mini" @click="setPreviewView('side')">侧视</el-button>
          <el-button size="mini" @click="setPreviewView('top')">俯视</el-button>
        </div>
        <div class="collapsible_group">
          <button type="button" class="group_toggle" @click="markerPanelExpanded = !markerPanelExpanded">
            <div>
              <div class="group_title">标定点</div>
              <div class="group_toggle__summary">
                当前选中：{{ activeMarkerLabel }}，默认收起以减少弹窗高度
              </div>
            </div>
            <i :class="markerPanelExpanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
          </button>
          <div v-show="markerPanelExpanded" class="group_body">
            <div class="view_group view_group_inner">
              <el-button
                size="mini"
                :type="activeMarkerType === 'anchor' ? 'primary' : 'default'"
                @click="setActiveMarker('anchor')"
              >
                红点：连接点
              </el-button>
              <el-button
                size="mini"
                :type="activeMarkerType === 'bendGuide' ? 'primary' : 'default'"
                @click="setActiveMarker('bendGuide')"
              >
                蓝点：花头中心
              </el-button>
              <el-button
                size="mini"
                :type="activeMarkerType === 'stemEntry' ? 'primary' : 'default'"
                @click="setActiveMarker('stemEntry')"
              >
                绿点：进入方向
              </el-button>
              <div class="value_tip">
                选中标定点后，点击模型表面可快速吸附；拖拽和数值微调用于精修。
              </div>
            </div>
            <div class="value_group" v-if="activeMarkerType === 'anchor' && anchorConfig">
              <div class="group_title">红点：根茎连接点</div>
              <div class="axis_row">
                <span>X</span>
                <el-input-number
                  v-model="anchorConfig.x"
                  size="mini"
                  :step="0.01"
                  :precision="3"
                  controls-position="right"
                  @change="updateMarkerFromConfig"
                />
              </div>
              <div class="axis_row">
                <span>Y</span>
                <el-input-number
                  v-model="anchorConfig.y"
                  size="mini"
                  :step="0.01"
                  :precision="3"
                  controls-position="right"
                  @change="updateMarkerFromConfig"
                />
              </div>
              <div class="axis_row">
                <span>Z</span>
                <el-input-number
                  v-model="anchorConfig.z"
                  size="mini"
                  :step="0.01"
                  :precision="3"
                  controls-position="right"
                  @change="updateMarkerFromConfig"
                />
              </div>
              <div class="nudge_group">
                <el-button size="mini" @click="nudgeAnchor('x', -0.01)">X-</el-button>
                <el-button size="mini" @click="nudgeAnchor('x', 0.01)">X+</el-button>
                <el-button size="mini" @click="nudgeAnchor('y', -0.01)">Y-</el-button>
                <el-button size="mini" @click="nudgeAnchor('y', 0.01)">Y+</el-button>
                <el-button size="mini" @click="nudgeAnchor('z', -0.01)">Z-</el-button>
                <el-button size="mini" @click="nudgeAnchor('z', 0.01)">Z+</el-button>
              </div>
            </div>
            <div class="value_group" v-if="activeMarkerType === 'bendGuide' && bendGuideConfig">
              <div class="group_title">蓝点：花头中心点</div>
              <div class="axis_row">
                <span>X</span>
                <el-input-number
                  v-model="bendGuideConfig.x"
                  size="mini"
                  :step="0.01"
                  :precision="3"
                  controls-position="right"
                  @change="updateBendGuideMarkerFromConfig"
                />
              </div>
              <div class="axis_row">
                <span>Y</span>
                <el-input-number
                  v-model="bendGuideConfig.y"
                  size="mini"
                  :step="0.01"
                  :precision="3"
                  controls-position="right"
                  @change="updateBendGuideMarkerFromConfig"
                />
              </div>
              <div class="axis_row">
                <span>Z</span>
                <el-input-number
                  v-model="bendGuideConfig.z"
                  size="mini"
                  :step="0.01"
                  :precision="3"
                  controls-position="right"
                  @change="updateBendGuideMarkerFromConfig"
                />
              </div>
              <div class="nudge_group">
                <el-button size="mini" @click="nudgeBendGuide('x', -0.01)">X-</el-button>
                <el-button size="mini" @click="nudgeBendGuide('x', 0.01)">X+</el-button>
                <el-button size="mini" @click="nudgeBendGuide('y', -0.01)">Y-</el-button>
                <el-button size="mini" @click="nudgeBendGuide('y', 0.01)">Y+</el-button>
                <el-button size="mini" @click="nudgeBendGuide('z', -0.01)">Z-</el-button>
                <el-button size="mini" @click="nudgeBendGuide('z', 0.01)">Z+</el-button>
              </div>
            </div>
            <div class="value_group" v-if="activeMarkerType === 'stemEntry' && stemEntryConfig">
              <div class="group_title">绿点：根茎进入方向</div>
              <div class="axis_row">
                <span>X</span>
                <el-input-number
                  v-model="stemEntryConfig.x"
                  size="mini"
                  :step="0.01"
                  :precision="3"
                  controls-position="right"
                  @change="updateStemEntryMarkerFromConfig"
                />
              </div>
              <div class="axis_row">
                <span>Y</span>
                <el-input-number
                  v-model="stemEntryConfig.y"
                  size="mini"
                  :step="0.01"
                  :precision="3"
                  controls-position="right"
                  @change="updateStemEntryMarkerFromConfig"
                />
              </div>
              <div class="axis_row">
                <span>Z</span>
                <el-input-number
                  v-model="stemEntryConfig.z"
                  size="mini"
                  :step="0.01"
                  :precision="3"
                  controls-position="right"
                  @change="updateStemEntryMarkerFromConfig"
                />
              </div>
              <div class="nudge_group">
                <el-button size="mini" @click="nudgeStemEntry('x', -0.01)">X-</el-button>
                <el-button size="mini" @click="nudgeStemEntry('x', 0.01)">X+</el-button>
                <el-button size="mini" @click="nudgeStemEntry('y', -0.01)">Y-</el-button>
                <el-button size="mini" @click="nudgeStemEntry('y', 0.01)">Y+</el-button>
                <el-button size="mini" @click="nudgeStemEntry('z', -0.01)">Z-</el-button>
                <el-button size="mini" @click="nudgeStemEntry('z', 0.01)">Z+</el-button>
              </div>
              <div class="value_tip">
                绿点越接近真实枝条延伸方向，根茎和 GLB 的接缝越自然。
              </div>
            </div>
          </div>
        </div>
        <div class="collapsible_group">
          <button type="button" class="group_toggle" @click="bendStrengthPanelExpanded = !bendStrengthPanelExpanded">
            <div>
              <div class="group_title">弯曲值</div>
              <div class="group_toggle__summary">
                当前值：{{ bendStrengthConfig }}，默认收起以减少弹窗高度
              </div>
            </div>
            <i :class="bendStrengthPanelExpanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
          </button>
          <div v-show="bendStrengthPanelExpanded" class="group_body">
            <div class="value_group value_group_inner">
              <div class="axis_row">
                <span>值</span>
                <el-input-number
                  v-model="bendStrengthConfig"
                  size="mini"
                  :min="0"
                  :max="3"
                  :step="0.1"
                  :precision="1"
                  controls-position="right"
                  @change="updatePreviewStemLine"
                />
              </div>
              <div class="value_tip">
                0 为完全直线，大于 0 时按红点和蓝点的关系生成自然弯曲。
              </div>
            </div>
          </div>
        </div>
        <div class="view_group">
          <div class="group_title">旋转预览</div>
          <el-button size="mini" @click="previewRotate(-1)">向左</el-button>
          <el-button size="mini" @click="previewRotate(1)">向右</el-button>
          <el-button size="mini" @click="resetPreviewRotation">归零</el-button>
        </div>
        <div class="validation_group">
          <div class="group_title">标注校验</div>
          <div
            v-for="item in validationMessages"
            :key="item.text"
            class="validation_item"
            :class="'is_' + item.level"
          >
            {{ item.text }}
          </div>
        </div>
        <div class="config_json_group">
          <div class="group_title">保存配置预览</div>
          <el-input type="textarea" :rows="6" readonly :value="modelConfigPreview" />
        </div>
        <div class="actions">
          <el-button size="small" @click="resetAnchor">重置为默认</el-button>
          <el-button size="small" @click="cancel">取消</el-button>
          <el-button type="primary" size="small" :disabled="!canConfirm" @click="confirmSave">
            确认保存标定
          </el-button>
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { TransformControls } from "three/examples/jsm/controls/TransformControls.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { MeshoptDecoder } from "three/examples/jsm/libs/meshopt_decoder.module.js";

const STORAGE_KEY = "oaGlbAnchorCalibrationV1";

export default {
  data() {
    return {
      dialogVisible: false,
      loading: false,
      resourceKey: "",
      resourceName: "",
      modelUrl: "",
      sourceModelConfig: {},
      anchorConfig: null,
      bendGuideConfig: null,
      stemEntryConfig: null,
      bendStrengthConfig: 1,
      defaultAnchorConfig: null,
      defaultBendGuideConfig: null,
      defaultStemEntryConfig: null,
      defaultBendStrengthConfig: 1,
      scene: null,
      camera: null,
      renderer: null,
      controls: null,
      transformControls: null,
      raycaster: null,
      pointerDownInfo: null,
      transformDragging: false,
      modelRoot: null,
      previewMeshes: [],
      marker: null,
      bendGuideMarker: null,
      stemEntryMarker: null,
      previewStemLine: null,
      previewRotationAngle: 0,
      activeMarkerType: "anchor",
      markerPanelExpanded: false,
      bendStrengthPanelExpanded: false,
      rafId: 0,
      modelBounds: null,
    };
  },
  computed: {
    activeMarkerLabel() {
      if (this.activeMarkerType === "bendGuide") {
        return "蓝点";
      }
      if (this.activeMarkerType === "stemEntry") {
        return "绿点";
      }
      return "红点";
    },
    validationMessages() {
      if (!this.anchorConfig || !this.bendGuideConfig || !this.stemEntryConfig) {
        return [{ level: "error", text: "请先完成红点、蓝点、绿点三处标定。" }];
      }
      const messages = [];
      const anchor = this.vectorFromConfig(this.anchorConfig);
      const headCenter = this.vectorFromConfig(this.bendGuideConfig);
      const stemEntry = this.vectorFromConfig(this.stemEntryConfig);
      const modelSize = this.modelBounds
        ? this.modelBounds.getSize(new THREE.Vector3())
        : new THREE.Vector3(1, 1, 1);
      const baseSize = Math.max(modelSize.x, modelSize.y, modelSize.z, 1);
      const minDistance = Math.max(baseSize * 0.018, 0.015);
      const warnDistance = Math.max(baseSize * 0.045, 0.035);
      const headDistance = anchor.distanceTo(headCenter);
      const entryDistance = anchor.distanceTo(stemEntry);

      if (headDistance < minDistance) {
        messages.push({
          level: "error",
          text: "蓝点与红点过近，花头中心方向无法稳定判断。",
        });
      } else if (headDistance < warnDistance) {
        messages.push({
          level: "warning",
          text: "蓝点离红点较近，建议放到花头/叶片视觉中心附近。",
        });
      }

      if (entryDistance < minDistance) {
        messages.push({
          level: "error",
          text: "绿点与红点过近，根茎进入方向无法稳定判断。",
        });
      } else if (entryDistance < warnDistance) {
        messages.push({
          level: "warning",
          text: "绿点离红点较近，根茎接入方向可能偏硬。",
        });
      }

      if (headDistance >= minDistance && entryDistance >= minDistance) {
        const toHead = headCenter.clone().sub(anchor).normalize();
        const toEntry = stemEntry.clone().sub(anchor).normalize();
        if (toHead.dot(toEntry) > 0.65) {
          messages.push({
            level: "warning",
            text: "绿点与蓝点方向过于接近，建议让绿点沿枝条进入方向远离花头。",
          });
        }
      }

      if ((parseFloat(this.bendStrengthConfig) || 0) <= 0) {
        messages.push({
          level: "warning",
          text: "当前弯曲值为 0，进入画布后根茎会保持直线。",
        });
      }

      if (!messages.length) {
        messages.push({
          level: "success",
          text: "标注点关系正常，可以保存为后台素材参数。",
        });
      }
      return messages;
    },
    canConfirm() {
      return !!(
        this.anchorConfig &&
        this.bendGuideConfig &&
        this.stemEntryConfig &&
        !this.validationMessages.some((item) => item.level === "error")
      );
    },
    modelConfigPreview() {
      return JSON.stringify(this.getOutputModelConfig(), null, 2);
    },
  },
  methods: {
    async open(payload) {
      this.dialogVisible = true;
      this.loading = true;
      this.resourceKey = String(payload.resourceKey || payload.id || payload.modelUrl || "");
      this.resourceName = payload.resourceName || payload.name || "";
      this.modelUrl = payload.modelUrl || "";
      this.sourceModelConfig = this.normalizeInputModelConfig(
        payload.modelConfigJson || payload.modelConfig
      );
      this.defaultAnchorConfig = this.getBaseAnchorConfig();
      this.defaultBendGuideConfig = this.getBaseBendGuideConfig();
      this.defaultStemEntryConfig = this.getBaseStemEntryConfig(
        this.defaultAnchorConfig,
        this.defaultBendGuideConfig
      );
      this.defaultBendStrengthConfig = this.getBaseBendStrengthConfig();
      this.anchorConfig = { ...this.getInitialAnchorConfig() };
      const initialBendGuideConfig = this.getInitialBendGuideConfig();
      this.bendGuideConfig = initialBendGuideConfig ? { ...initialBendGuideConfig } : null;
      const initialStemEntryConfig = this.getInitialStemEntryConfig();
      this.stemEntryConfig = initialStemEntryConfig ? { ...initialStemEntryConfig } : null;
      this.bendStrengthConfig = this.getInitialBendStrengthConfig();
      this.activeMarkerType = "anchor";
      this.markerPanelExpanded = false;
      this.bendStrengthPanelExpanded = false;
      await this.$nextTick();
      this.initPreview();
    },
    normalizeInputModelConfig(input) {
      if (!input) {
        return {};
      }
      if (typeof input === "string") {
        try {
          return JSON.parse(input);
        } catch (error) {
          return {};
        }
      }
      return { ...input };
    },
    getStoredOverrides() {
      try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
      } catch (error) {
        return {};
      }
    },
    setStoredOverride(cacheKey, anchorConfig, bendGuideConfig, stemEntryConfig, bendStrengthConfig) {
      const cache = this.getStoredOverrides();
      cache[cacheKey] = {
        anchor: anchorConfig,
        headCenter: bendGuideConfig,
        bendGuide: bendGuideConfig,
        stemEntry: stemEntryConfig,
        bendStrength: bendStrengthConfig,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
    },
    vectorFromConfig(config) {
      return new THREE.Vector3(
        parseFloat((config && config.x) || 0),
        parseFloat((config && config.y) || 0),
        parseFloat((config && config.z) || 0)
      );
    },
    normalizePointConfig(config) {
      return {
        x: +(parseFloat((config && config.x) || 0)).toFixed(3),
        y: +(parseFloat((config && config.y) || 0)).toFixed(3),
        z: +(parseFloat((config && config.z) || 0)).toFixed(3),
      };
    },
    getOutputModelConfig() {
      if (!this.anchorConfig || !this.bendGuideConfig || !this.stemEntryConfig) {
        return {};
      }
      const modelConfig = this.sourceModelConfig || {};
      const anchor = this.normalizePointConfig(this.anchorConfig);
      const headCenter = this.normalizePointConfig(this.bendGuideConfig);
      const stemEntry = this.normalizePointConfig(this.stemEntryConfig);
      const supportedTools = Array.isArray(modelConfig.supportedTools)
        ? Array.from(new Set([...modelConfig.supportedTools, "directionRotate"]))
        : ["directionRotate"];
      const getNumberOrDefault = (value, fallback) => {
        const parsed = parseFloat(value);
        return Number.isNaN(parsed) ? fallback : parsed;
      };
      return {
        modelConfigVersion: 1,
        stemAxisFromMarkers: true,
        supportedTools,
        rootAnchor: { ...anchor },
        stemAnchor: { ...anchor },
        headCenter: { ...headCenter },
        bendGuide: { ...headCenter },
        stemEntry: { ...stemEntry },
        bendStrength: Math.max(0, parseFloat(this.bendStrengthConfig) || 0),
        headAngleStep: getNumberOrDefault(modelConfig.headAngleStep, 15),
        headAngleMin: getNumberOrDefault(modelConfig.headAngleMin, -180),
        headAngleMax: getNumberOrDefault(modelConfig.headAngleMax, 180),
      };
    },
    getBaseAnchorConfig() {
      const modelConfig = this.sourceModelConfig || {};
      return {
        x: parseFloat(((modelConfig.stemAnchor || modelConfig.rootAnchor || {}).x) || 0),
        y: parseFloat(((modelConfig.stemAnchor || modelConfig.rootAnchor || {}).y) || 0),
        z: parseFloat(((modelConfig.stemAnchor || modelConfig.rootAnchor || {}).z) || 0),
      };
    },
    getInitialAnchorConfig() {
      const stored = this.getStoredOverrides()[this.resourceKey];
      if (stored && stored.anchor) {
        return stored.anchor;
      }
      return this.getBaseAnchorConfig();
    },
    getBaseBendGuideConfig() {
      const modelConfig = this.sourceModelConfig || {};
      const bendGuide = modelConfig.headCenter || modelConfig.bendGuide || modelConfig.headGuide || {};
      const hasBendGuide =
        bendGuide.x !== undefined || bendGuide.y !== undefined || bendGuide.z !== undefined;
      if (hasBendGuide) {
        return {
          x: parseFloat(bendGuide.x) || 0,
          y: parseFloat(bendGuide.y) || 0,
          z: parseFloat(bendGuide.z) || 0,
        };
      }
      if (this.modelBounds) {
        return {
          x: 0,
          y: +(this.modelBounds.getCenter(new THREE.Vector3()).y - this.modelBounds.min.y).toFixed(3),
          z: 0,
        };
      }
      return null;
    },
    getInitialBendGuideConfig() {
      const stored = this.getStoredOverrides()[this.resourceKey];
      if (stored && stored.headCenter) {
        return stored.headCenter;
      }
      if (stored && stored.bendGuide) {
        return stored.bendGuide;
      }
      return this.getBaseBendGuideConfig();
    },
    getBaseStemEntryConfig(anchorConfig = null, bendGuideConfig = null) {
      const modelConfig = this.sourceModelConfig || {};
      const stemEntry = modelConfig.stemEntry || modelConfig.stemGuide || modelConfig.entryGuide || {};
      const hasStemEntry =
        stemEntry.x !== undefined || stemEntry.y !== undefined || stemEntry.z !== undefined;
      if (hasStemEntry) {
        return {
          x: parseFloat(stemEntry.x) || 0,
          y: parseFloat(stemEntry.y) || 0,
          z: parseFloat(stemEntry.z) || 0,
        };
      }

      const anchor = anchorConfig || this.anchorConfig || this.getBaseAnchorConfig();
      const headCenter = bendGuideConfig || this.bendGuideConfig || this.getBaseBendGuideConfig();
      if (anchor && headCenter) {
        const direction = new THREE.Vector3(
          (parseFloat(anchor.x) || 0) - (parseFloat(headCenter.x) || 0),
          (parseFloat(anchor.y) || 0) - (parseFloat(headCenter.y) || 0),
          (parseFloat(anchor.z) || 0) - (parseFloat(headCenter.z) || 0)
        );
        if (direction.lengthSq() > 0.0001) {
          const stemEntryVector = new THREE.Vector3(
            parseFloat(anchor.x) || 0,
            parseFloat(anchor.y) || 0,
            parseFloat(anchor.z) || 0
          ).add(direction.multiplyScalar(0.6));
          return {
            x: +stemEntryVector.x.toFixed(3),
            y: +stemEntryVector.y.toFixed(3),
            z: +stemEntryVector.z.toFixed(3),
          };
        }
      }

      return {
        x: +((parseFloat(anchor.x) || 0)).toFixed(3),
        y: +((parseFloat(anchor.y) || 0) - 0.25).toFixed(3),
        z: +((parseFloat(anchor.z) || 0)).toFixed(3),
      };
    },
    getInitialStemEntryConfig() {
      const stored = this.getStoredOverrides()[this.resourceKey];
      if (stored && stored.stemEntry) {
        return stored.stemEntry;
      }
      return this.getBaseStemEntryConfig(this.anchorConfig, this.bendGuideConfig);
    },
    getBaseBendStrengthConfig() {
      const modelConfig = this.sourceModelConfig || {};
      const bendStrength = parseFloat(modelConfig.bendStrength);
      return Number.isNaN(bendStrength) ? 1 : Math.max(0, bendStrength);
    },
    getInitialBendStrengthConfig() {
      const stored = this.getStoredOverrides()[this.resourceKey];
      if (stored && stored.bendStrength !== undefined) {
        return Math.max(0, parseFloat(stored.bendStrength) || 0);
      }
      return this.getBaseBendStrengthConfig();
    },
    createMarkerTexture(color = "#ff6158") {
      const canvas = document.createElement("canvas");
      canvas.width = 128;
      canvas.height = 128;
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, 128, 128);
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(64, 64, 46, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = 8;
      ctx.beginPath();
      ctx.moveTo(64, 28);
      ctx.lineTo(64, 100);
      ctx.moveTo(28, 64);
      ctx.lineTo(100, 64);
      ctx.stroke();
      return new THREE.CanvasTexture(canvas);
    },
    createPointMarker(color, textureColor) {
      const marker = new THREE.Group();
      const sphere = new THREE.Mesh(
        new THREE.SphereGeometry(0.035, 24, 24),
        new THREE.MeshBasicMaterial({
          color,
          depthTest: false,
          depthWrite: false,
        })
      );
      sphere.renderOrder = 1000;
      const label = new THREE.Sprite(
        new THREE.SpriteMaterial({
          map: this.createMarkerTexture(textureColor),
          transparent: true,
          depthTest: false,
          depthWrite: false,
        })
      );
      label.scale.set(0.18, 0.18, 0.18);
      marker.add(sphere);
      marker.add(label);
      marker.renderOrder = 999;
      return marker;
    },
    initPreview() {
      this.disposePreview();
      const mount = this.$refs.preview;
      if (!mount || !this.modelUrl) {
        this.loading = false;
        return;
      }

      this.scene = new THREE.Scene();
      this.scene.background = new THREE.Color(0xf8f7f3);

      const width = mount.clientWidth || 640;
      const height = mount.clientHeight || 520;
      this.camera = new THREE.PerspectiveCamera(35, width / height, 0.01, 100);
      this.camera.position.set(0.8, 0.8, 1.2);

      this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      this.renderer.setPixelRatio(window.devicePixelRatio || 1);
      this.renderer.setSize(width, height);
      mount.innerHTML = "";
      mount.appendChild(this.renderer.domElement);
      this.renderer.domElement.addEventListener("pointerdown", this.handlePreviewPointerDown);
      this.renderer.domElement.addEventListener("pointerup", this.handlePreviewPointerUp);

      this.controls = new OrbitControls(this.camera, this.renderer.domElement);
      this.controls.enableDamping = true;
      this.controls.dampingFactor = 0.08;
      this.raycaster = new THREE.Raycaster();

      const ambient = new THREE.AmbientLight(0xffffff, 1.25);
      const light = new THREE.DirectionalLight(0xffffff, 1.1);
      light.position.set(2, 3, 2);
      const light2 = new THREE.DirectionalLight(0xffffff, 0.55);
      light2.position.set(-2, 1.5, -2);
      this.scene.add(ambient, light, light2);

      const grid = new THREE.GridHelper(2, 20, 0xcfcfcf, 0xe7e7e7);
      grid.position.y = -0.01;
      this.scene.add(grid);

      this.marker = this.createPointMarker(0xff6158, "rgba(255, 97, 88, 0.9)");
      this.bendGuideMarker = this.createPointMarker(0x3f8cff, "rgba(63, 140, 255, 0.9)");
      this.stemEntryMarker = this.createPointMarker(0x35b86b, "rgba(53, 184, 107, 0.9)");
      this.scene.add(this.marker);
      this.scene.add(this.bendGuideMarker);
      this.scene.add(this.stemEntryMarker);

      this.previewStemLine = new THREE.Line(
        new THREE.BufferGeometry(),
        new THREE.LineBasicMaterial({
          color: 0xff6158,
          depthTest: false,
        })
      );
      this.previewStemLine.renderOrder = 998;
      this.scene.add(this.previewStemLine);

      this.transformControls = new TransformControls(this.camera, this.renderer.domElement);
      this.transformControls.setMode("translate");
      this.transformControls.setSize(0.65);
      this.transformControls.attach(this.getActiveMarker());
      this.transformControls.addEventListener("dragging-changed", (event) => {
        this.transformDragging = event.value;
        if (this.controls) {
          this.controls.enabled = !event.value;
        }
      });
      this.transformControls.addEventListener("objectChange", () => {
        this.updateActiveConfigFromMarker();
      });
      this.transformControls.addEventListener("change", () => {
        this.updatePreviewStemLine();
      });
      this.scene.add(this.transformControls);

      const loader = new GLTFLoader();
      loader.setMeshoptDecoder(MeshoptDecoder);
      loader.load(
        this.modelUrl,
        (gltf) => {
          this.modelRoot = (gltf.scene || gltf.scenes[0] || new THREE.Group()).clone(true);
          this.previewMeshes = [];
          this.modelRoot.traverse((node) => {
            if (!node.isMesh) {
              return;
            }
            node.castShadow = true;
            node.receiveShadow = true;
            if (Array.isArray(node.material)) {
              node.material = node.material.map((material) => {
                const cloned = material.clone();
                cloned.side = THREE.DoubleSide;
                return cloned;
              });
            } else if (node.material) {
              node.material = node.material.clone();
              node.material.side = THREE.DoubleSide;
            }
            this.previewMeshes.push(node);
          });
          this.scene.add(this.modelRoot);
          this.modelBounds = new THREE.Box3().setFromObject(this.modelRoot);
          const baseBendGuideConfig = this.getBaseBendGuideConfig();
          this.defaultBendGuideConfig = this.getValidatedAnchorConfig(
            this.hasPointValue(this.defaultBendGuideConfig)
              ? this.defaultBendGuideConfig
              : baseBendGuideConfig,
            false,
            baseBendGuideConfig
          );
          this.anchorConfig = this.getValidatedAnchorConfig(this.anchorConfig);
          this.bendGuideConfig = this.getValidatedAnchorConfig(
            this.bendGuideConfig || this.defaultBendGuideConfig,
            false,
            this.defaultBendGuideConfig
          );
          const baseStemEntryConfig = this.getBaseStemEntryConfig(
            this.anchorConfig,
            this.bendGuideConfig
          );
          this.defaultStemEntryConfig = this.getValidatedAnchorConfig(
            this.hasPointValue(this.defaultStemEntryConfig)
              ? this.defaultStemEntryConfig
              : baseStemEntryConfig,
            false,
            baseStemEntryConfig
          );
          this.stemEntryConfig = this.getValidatedAnchorConfig(
            this.stemEntryConfig || this.defaultStemEntryConfig,
            false,
            this.defaultStemEntryConfig
          );
          this.defaultAnchorConfig = this.getValidatedAnchorConfig(
            this.defaultAnchorConfig,
            true
          );
          this.fitCameraToModel();
          this.updateMarkerFromConfig();
          this.updateBendGuideMarkerFromConfig();
          this.updateStemEntryMarkerFromConfig();
          this.setActiveMarker(this.activeMarkerType);
          this.renderLoop();
          this.loading = false;
        },
        undefined,
        () => {
          this.$message.error("GLB预览加载失败");
          this.loading = false;
          this.dialogVisible = false;
        }
      );
    },
    hasPointValue(point) {
      return !!point && (point.x !== undefined || point.y !== undefined || point.z !== undefined);
    },
    getValidatedAnchorConfig(anchorConfig, useBaseFallback = false, customFallback = null) {
      if (!this.modelBounds) {
        return anchorConfig
          ? { ...anchorConfig }
          : { ...(customFallback || this.getBaseAnchorConfig()) };
      }
      const fallback =
        customFallback ||
        (useBaseFallback
          ? this.getBaseAnchorConfig()
          : this.defaultAnchorConfig || this.getBaseAnchorConfig());
      const candidate = anchorConfig ? { ...anchorConfig } : { ...fallback };
      const center = this.modelBounds.getCenter(new THREE.Vector3());
      const size = this.modelBounds.getSize(new THREE.Vector3());
      const resolved = new THREE.Vector3(
        center.x + (parseFloat(candidate.x) || 0),
        this.modelBounds.min.y + (parseFloat(candidate.y) || 0),
        center.z + (parseFloat(candidate.z) || 0)
      );
      const padding = Math.max(Math.max(size.x, size.y, size.z) * 0.2, 0.08);
      const valid =
        resolved.x >= this.modelBounds.min.x - padding &&
        resolved.x <= this.modelBounds.max.x + padding &&
        resolved.y >= this.modelBounds.min.y - padding &&
        resolved.y <= this.modelBounds.max.y + padding &&
        resolved.z >= this.modelBounds.min.z - padding &&
        resolved.z <= this.modelBounds.max.z + padding;
      return valid ? candidate : { ...fallback };
    },
    getAnchorEditRange() {
      if (!this.modelBounds) {
        return null;
      }
      const center = this.modelBounds.getCenter(new THREE.Vector3());
      const size = this.modelBounds.getSize(new THREE.Vector3());
      const padding = Math.max(Math.max(size.x, size.y, size.z) * 0.25, 0.08);
      return {
        xMin: this.modelBounds.min.x - padding - center.x,
        xMax: this.modelBounds.max.x + padding - center.x,
        yMin: -padding,
        yMax: this.modelBounds.max.y + padding - this.modelBounds.min.y,
        zMin: this.modelBounds.min.z - padding - center.z,
        zMax: this.modelBounds.max.z + padding - center.z,
      };
    },
    constrainAnchorConfig(anchorConfig) {
      const range = this.getAnchorEditRange();
      if (!range) {
        return anchorConfig ? { ...anchorConfig } : { ...this.getBaseAnchorConfig() };
      }
      const clampNumber = (value, min, max) => {
        const parsed = parseFloat(value) || 0;
        return +THREE.MathUtils.clamp(parsed, min, max).toFixed(3);
      };
      return {
        x: clampNumber(anchorConfig.x, range.xMin, range.xMax),
        y: clampNumber(anchorConfig.y, range.yMin, range.yMax),
        z: clampNumber(anchorConfig.z, range.zMin, range.zMax),
      };
    },
    setMarkerPositionFromAnchor() {
      if (!this.marker || !this.modelBounds || !this.anchorConfig) {
        return;
      }
      this.setMarkerPositionFromConfig(this.marker, this.anchorConfig);
    },
    setBendGuideMarkerPositionFromConfig() {
      if (!this.bendGuideMarker || !this.modelBounds || !this.bendGuideConfig) {
        return;
      }
      this.setMarkerPositionFromConfig(this.bendGuideMarker, this.bendGuideConfig);
    },
    setStemEntryMarkerPositionFromConfig() {
      if (!this.stemEntryMarker || !this.modelBounds || !this.stemEntryConfig) {
        return;
      }
      this.setMarkerPositionFromConfig(this.stemEntryMarker, this.stemEntryConfig);
    },
    setMarkerPositionFromConfig(marker, config) {
      const center = this.modelBounds.getCenter(new THREE.Vector3());
      marker.position.set(
        center.x + config.x,
        this.modelBounds.min.y + config.y,
        center.z + config.z
      );
    },
    getConfigFromMarker(marker) {
      const localPoint = marker.position.clone();
      const center = this.modelBounds.getCenter(new THREE.Vector3());
      return this.constrainAnchorConfig({
        x: +(localPoint.x - center.x).toFixed(3),
        y: +(localPoint.y - this.modelBounds.min.y).toFixed(3),
        z: +(localPoint.z - center.z).toFixed(3),
      });
    },
    getActiveMarker() {
      if (this.activeMarkerType === "bendGuide") {
        return this.bendGuideMarker;
      }
      if (this.activeMarkerType === "stemEntry") {
        return this.stemEntryMarker;
      }
      return this.marker;
    },
    setActiveMarker(type) {
      this.activeMarkerType = ["bendGuide", "stemEntry"].includes(type) ? type : "anchor";
      if (this.transformControls && this.getActiveMarker()) {
        this.transformControls.attach(this.getActiveMarker());
      }
      this.updatePreviewStemLine();
    },
    updateActiveConfigFromMarker() {
      if (this.activeMarkerType === "bendGuide") {
        this.updateBendGuideFromMarker();
        return;
      }
      if (this.activeMarkerType === "stemEntry") {
        this.updateStemEntryFromMarker();
        return;
      }
      this.updateAnchorFromMarker();
    },
    updateMarkerFromConfig() {
      if (!this.marker || !this.modelBounds || !this.anchorConfig) {
        return;
      }
      this.resetPreviewModelRotation();
      this.anchorConfig = this.constrainAnchorConfig(this.anchorConfig);
      this.setMarkerPositionFromAnchor();
      this.updatePreviewStemLine();
    },
    updateBendGuideMarkerFromConfig() {
      if (!this.bendGuideMarker || !this.modelBounds || !this.bendGuideConfig) {
        return;
      }
      this.resetPreviewModelRotation();
      this.bendGuideConfig = this.constrainAnchorConfig(this.bendGuideConfig);
      this.setBendGuideMarkerPositionFromConfig();
      this.updatePreviewStemLine();
    },
    updateStemEntryMarkerFromConfig() {
      if (!this.stemEntryMarker || !this.modelBounds || !this.stemEntryConfig) {
        return;
      }
      this.resetPreviewModelRotation();
      this.stemEntryConfig = this.constrainAnchorConfig(this.stemEntryConfig);
      this.setStemEntryMarkerPositionFromConfig();
      this.updatePreviewStemLine();
    },
    updateAnchorFromMarker() {
      if (!this.marker || !this.modelBounds) {
        return;
      }
      this.resetPreviewModelRotation();
      this.anchorConfig = this.getConfigFromMarker(this.marker);
      this.setMarkerPositionFromAnchor();
      this.updatePreviewStemLine();
    },
    updateBendGuideFromMarker() {
      if (!this.bendGuideMarker || !this.modelBounds) {
        return;
      }
      this.resetPreviewModelRotation();
      this.bendGuideConfig = this.getConfigFromMarker(this.bendGuideMarker);
      this.setBendGuideMarkerPositionFromConfig();
      this.updatePreviewStemLine();
    },
    updateStemEntryFromMarker() {
      if (!this.stemEntryMarker || !this.modelBounds) {
        return;
      }
      this.resetPreviewModelRotation();
      this.stemEntryConfig = this.getConfigFromMarker(this.stemEntryMarker);
      this.setStemEntryMarkerPositionFromConfig();
      this.updatePreviewStemLine();
    },
    handlePreviewPointerDown(event) {
      this.pointerDownInfo = {
        x: event.clientX,
        y: event.clientY,
        time: Date.now(),
      };
    },
    handlePreviewPointerUp(event) {
      if (!this.pointerDownInfo || this.transformDragging) {
        return;
      }
      const deltaX = event.clientX - this.pointerDownInfo.x;
      const deltaY = event.clientY - this.pointerDownInfo.y;
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      const duration = Date.now() - this.pointerDownInfo.time;
      this.pointerDownInfo = null;
      if (distance > 5 || duration > 450) {
        return;
      }
      this.snapActiveMarkerToSurface(event);
    },
    snapActiveMarkerToSurface(event) {
      if (!this.raycaster || !this.camera || !this.renderer || !this.previewMeshes.length) {
        return;
      }
      if (this.previewRotationAngle !== 0) {
        this.$message.warning("请先点击“归零”再吸附标定点，避免旋转预览影响坐标。");
        return;
      }
      const rect = this.renderer.domElement.getBoundingClientRect();
      const pointer = new THREE.Vector2(
        ((event.clientX - rect.left) / rect.width) * 2 - 1,
        -((event.clientY - rect.top) / rect.height) * 2 + 1
      );
      this.raycaster.setFromCamera(pointer, this.camera);
      const intersects = this.raycaster.intersectObjects(this.previewMeshes, true);
      if (!intersects.length) {
        return;
      }
      const activeMarker = this.getActiveMarker();
      if (!activeMarker) {
        return;
      }
      activeMarker.position.copy(intersects[0].point);
      this.updateActiveConfigFromMarker();
    },
    nudgeAnchor(axis, delta) {
      if (!this.anchorConfig || !["x", "y", "z"].includes(axis)) {
        return;
      }
      this.anchorConfig = {
        ...this.anchorConfig,
        [axis]: +((parseFloat(this.anchorConfig[axis]) || 0) + delta).toFixed(3),
      };
      this.updateMarkerFromConfig();
    },
    nudgeBendGuide(axis, delta) {
      if (!this.bendGuideConfig || !["x", "y", "z"].includes(axis)) {
        return;
      }
      this.bendGuideConfig = {
        ...this.bendGuideConfig,
        [axis]: +((parseFloat(this.bendGuideConfig[axis]) || 0) + delta).toFixed(3),
      };
      this.updateBendGuideMarkerFromConfig();
    },
    nudgeStemEntry(axis, delta) {
      if (!this.stemEntryConfig || !["x", "y", "z"].includes(axis)) {
        return;
      }
      this.stemEntryConfig = {
        ...this.stemEntryConfig,
        [axis]: +((parseFloat(this.stemEntryConfig[axis]) || 0) + delta).toFixed(3),
      };
      this.updateStemEntryMarkerFromConfig();
    },
    getPreviewStemAxis() {
      if (this.marker && this.stemEntryMarker) {
        const markerAxis = this.marker.position.clone().sub(this.stemEntryMarker.position);
        if (markerAxis.lengthSq() > 0.0001) {
          return markerAxis.normalize();
        }
      }
      if (this.marker && this.bendGuideMarker) {
        const markerAxis = this.marker.position.clone().sub(this.bendGuideMarker.position);
        if (markerAxis.lengthSq() > 0.0001) {
          return markerAxis.normalize();
        }
      }
      return new THREE.Vector3(0, 1, 0);
    },
    updatePreviewStemLine() {
      if (
        !this.previewStemLine ||
        !this.marker ||
        !this.bendGuideMarker ||
        !this.stemEntryMarker ||
        !this.modelBounds
      ) {
        return;
      }
      const size = this.modelBounds.getSize(new THREE.Vector3());
      const length = Math.max(Math.max(size.x, size.y, size.z) * 0.45, 0.25);
      if ((parseFloat(this.bendStrengthConfig) || 0) <= 0) {
        const axis = this.getPreviewStemAxis();
        const start = this.marker.position.clone().addScaledVector(axis, -length);
        this.previewStemLine.geometry.dispose();
        this.previewStemLine.geometry = new THREE.BufferGeometry().setFromPoints([
          start,
          this.marker.position.clone(),
        ]);
        return;
      }
      const tipTangent = this.stemEntryMarker.position.clone().sub(this.marker.position);
      if (tipTangent.lengthSq() < 0.0001) {
        tipTangent.copy(this.getPreviewStemAxis());
      }
      const tangentPoint = this.marker.position
        .clone()
        .addScaledVector(tipTangent.normalize(), length * (parseFloat(this.bendStrengthConfig) || 1));
      this.previewStemLine.geometry.dispose();
      this.previewStemLine.geometry = new THREE.BufferGeometry().setFromPoints([
        this.bendGuideMarker.position.clone(),
        this.marker.position.clone(),
        tangentPoint,
        this.stemEntryMarker.position.clone(),
      ]);
    },
    setPreviewView(type) {
      if (!this.modelBounds || !this.camera || !this.controls) {
        return;
      }
      const center = this.modelBounds.getCenter(new THREE.Vector3());
      const size = this.modelBounds.getSize(new THREE.Vector3());
      const radius = Math.max(size.x, size.y, size.z) * 1.8 || 1;
      const positions = {
        front: new THREE.Vector3(center.x, center.y, center.z + radius),
        side: new THREE.Vector3(center.x + radius, center.y, center.z),
        top: new THREE.Vector3(center.x, center.y + radius, center.z + 0.001),
      };
      this.controls.target.copy(center);
      this.camera.position.copy(positions[type] || positions.front);
      this.camera.lookAt(center);
      this.camera.updateProjectionMatrix();
      this.controls.update();
    },
    previewRotate(direction) {
      this.previewRotationAngle += direction * 15;
      this.applyPreviewRotation();
    },
    resetPreviewRotation() {
      this.previewRotationAngle = 0;
      this.applyPreviewRotation();
    },
    resetPreviewModelRotation() {
      this.previewRotationAngle = 0;
      if (!this.modelRoot) {
        return;
      }
      this.modelRoot.position.set(0, 0, 0);
      this.modelRoot.quaternion.identity();
    },
    applyPreviewRotation() {
      if (!this.modelRoot || !this.marker) {
        return;
      }
      const angle = THREE.MathUtils.degToRad(this.previewRotationAngle);
      const anchor = this.marker.position.clone();
      const axis = this.getPreviewStemAxis();
      this.modelRoot.position.copy(anchor.clone().negate());
      this.modelRoot.position.applyAxisAngle(axis, angle);
      this.modelRoot.position.add(anchor);
      this.modelRoot.quaternion.setFromAxisAngle(axis, angle);
    },
    fitCameraToModel() {
      if (!this.modelBounds || !this.camera || !this.controls) {
        return;
      }
      const center = this.modelBounds.getCenter(new THREE.Vector3());
      const size = this.modelBounds.getSize(new THREE.Vector3());
      const radius = Math.max(size.x, size.y, size.z) * 0.8 || 0.6;
      this.controls.target.copy(center);
      this.camera.position.set(
        center.x + radius * 1.2,
        center.y + radius * 1.1,
        center.z + radius * 1.6
      );
      this.camera.near = 0.01;
      this.camera.far = 20;
      this.camera.updateProjectionMatrix();
      this.controls.update();
    },
    renderLoop() {
      cancelAnimationFrame(this.rafId);
      const render = () => {
        this.rafId = requestAnimationFrame(render);
        if (this.controls) {
          this.controls.update();
        }
        if (this.renderer && this.scene && this.camera) {
          this.renderer.render(this.scene, this.camera);
        }
      };
      render();
    },
    resetAnchor() {
      this.anchorConfig = { ...this.defaultAnchorConfig };
      this.bendGuideConfig = { ...this.defaultBendGuideConfig };
      this.stemEntryConfig = { ...this.defaultStemEntryConfig };
      this.bendStrengthConfig = this.defaultBendStrengthConfig;
      this.updateMarkerFromConfig();
      this.updateBendGuideMarkerFromConfig();
      this.updateStemEntryMarkerFromConfig();
    },
    cancel() {
      this.dialogVisible = false;
    },
    confirmSave() {
      if (!this.anchorConfig || !this.bendGuideConfig || !this.stemEntryConfig) {
        return;
      }
      this.setStoredOverride(
        this.resourceKey,
        this.anchorConfig,
        this.bendGuideConfig,
        this.stemEntryConfig,
        this.bendStrengthConfig
      );
      const outputModelConfig = this.getOutputModelConfig();
      this.$emit("confirm", {
        modelConfig: outputModelConfig,
        modelConfigJson: JSON.stringify(outputModelConfig),
      });
      this.dialogVisible = false;
    },
    handleClosed() {
      this.disposePreview();
      this.loading = false;
      this.resourceKey = "";
      this.resourceName = "";
      this.modelUrl = "";
      this.sourceModelConfig = {};
      this.anchorConfig = null;
      this.bendGuideConfig = null;
      this.stemEntryConfig = null;
      this.bendStrengthConfig = 1;
      this.defaultAnchorConfig = null;
      this.defaultBendGuideConfig = null;
      this.defaultStemEntryConfig = null;
      this.defaultBendStrengthConfig = 1;
      this.activeMarkerType = "anchor";
      this.markerPanelExpanded = false;
      this.bendStrengthPanelExpanded = false;
      this.pointerDownInfo = null;
      this.transformDragging = false;
    },
    disposePreview() {
      cancelAnimationFrame(this.rafId);
      this.rafId = 0;
      if (this.transformControls) {
        this.transformControls.detach();
        if (this.transformControls.parent) {
          this.transformControls.parent.remove(this.transformControls);
        }
        this.transformControls.dispose && this.transformControls.dispose();
        this.transformControls = null;
      }
      if (this.controls) {
        this.controls.dispose();
        this.controls = null;
      }
      if (this.previewStemLine) {
        if (this.previewStemLine.parent) {
          this.previewStemLine.parent.remove(this.previewStemLine);
        }
        this.previewStemLine.geometry && this.previewStemLine.geometry.dispose();
        this.previewStemLine.material && this.previewStemLine.material.dispose();
        this.previewStemLine = null;
      }
      if (this.marker) {
        this.disposeMarker(this.marker);
        this.marker = null;
      }
      if (this.bendGuideMarker) {
        this.disposeMarker(this.bendGuideMarker);
        this.bendGuideMarker = null;
      }
      if (this.stemEntryMarker) {
        this.disposeMarker(this.stemEntryMarker);
        this.stemEntryMarker = null;
      }
      if (this.modelRoot) {
        this.disposeObject3D(this.modelRoot);
        this.modelRoot = null;
      }
      if (this.renderer) {
        if (this.renderer.domElement) {
          this.renderer.domElement.removeEventListener("pointerdown", this.handlePreviewPointerDown);
          this.renderer.domElement.removeEventListener("pointerup", this.handlePreviewPointerUp);
        }
        this.renderer.dispose();
        this.renderer.forceContextLoss && this.renderer.forceContextLoss();
        this.renderer = null;
      }
      if (this.$refs.preview) {
        this.$refs.preview.innerHTML = "";
      }
      this.scene = null;
      this.camera = null;
      this.raycaster = null;
      this.previewMeshes = [];
      this.modelBounds = null;
      this.previewRotationAngle = 0;
    },
    disposeObject3D(target) {
      target.traverse((node) => {
        if (node.geometry) {
          node.geometry.dispose();
        }
        const materials = Array.isArray(node.material)
          ? node.material
          : node.material
            ? [node.material]
            : [];
        materials.forEach((material) => {
          if (material.map) {
            material.map.dispose();
          }
          material.dispose && material.dispose();
        });
      });
      if (target.parent) {
        target.parent.remove(target);
      }
    },
    disposeMarker(marker) {
      if (marker.parent) {
        marker.parent.remove(marker);
      }
      marker.traverse((node) => {
        if (node.geometry) {
          node.geometry.dispose();
        }
        const materials = Array.isArray(node.material)
          ? node.material
          : node.material
            ? [node.material]
            : [];
        materials.forEach((material) => {
          if (material.map) {
            material.map.dispose();
          }
          material.dispose && material.dispose();
        });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.calibration_wrap {
  display: flex;
  gap: 20px;
  min-height: 560px;
}

.preview_panel {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.preview_canvas {
  flex: 1;
  min-height: 520px;
  border: 1px solid #e6e2d8;
  border-radius: 10px;
  overflow: hidden;
  background: linear-gradient(180deg, #fbfaf6 0%, #f4efe3 100%);
}

.preview_tip {
  margin-top: 10px;
  color: #8b7b64;
  font-size: 12px;
  line-height: 18px;
}

.config_panel {
  width: 280px;
  padding: 4px 0;
}

.material_name {
  font-size: 18px;
  font-weight: 600;
  color: #3e2e1f;
  line-height: 26px;
}

.desc {
  margin-top: 14px;
  color: #766758;
  font-size: 13px;
  line-height: 22px;
}

.view_group {
  margin-top: 20px;
  padding-bottom: 14px;
  border-bottom: 1px solid #eee5d7;

  ::v-deep .el-button {
    margin: 0 6px 8px 0;
  }
}

.view_group_inner {
  margin-top: 0;
  padding-bottom: 0;
  border-bottom: 0;
}

.collapsible_group {
  margin-top: 20px;
  border: 1px solid #eee5d7;
  border-radius: 12px;
  background: #fbf8f1;
}

.group_toggle {
  width: 100%;
  padding: 14px 16px;
  border: 0;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: space-between;
  text-align: left;
  cursor: pointer;
}

.group_toggle i {
  font-size: 14px;
  color: #8a715a;
  flex-shrink: 0;
  margin-left: 12px;
}

.group_toggle__summary {
  margin-top: 4px;
  font-size: 12px;
  color: #9b8b77;
  line-height: 18px;
}

.group_body {
  padding: 0 16px 16px;
}

.value_group {
  margin-top: 22px;
  padding: 14px 16px;
  background: #f7f3ea;
  border-radius: 10px;
  color: #5e4f41;
  line-height: 24px;
}

.value_group_inner {
  margin-top: 0;
}

.group_title {
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 600;
}

.axis_row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;

  span {
    width: 20px;
    color: #8a715a;
    font-weight: 600;
  }

  ::v-deep .el-input-number {
    width: 180px;
  }
}

.nudge_group {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-top: 14px;

  ::v-deep .el-button {
    margin: 0;
    padding-left: 0;
    padding-right: 0;
  }
}

.value_tip {
  margin-top: 8px;
  color: #9b8b77;
  font-size: 12px;
  line-height: 18px;
}

.validation_group,
.config_json_group {
  margin-top: 18px;
  padding: 12px 14px;
  border-radius: 10px;
  background: #fbf8f1;
  border: 1px solid #eee5d7;
}

.validation_item {
  margin-top: 8px;
  padding: 7px 8px;
  border-radius: 7px;
  font-size: 12px;
  line-height: 17px;

  &.is_success {
    color: #2f7d4d;
    background: rgba(53, 184, 107, 0.12);
  }

  &.is_warning {
    color: #9a6a1f;
    background: rgba(213, 151, 48, 0.14);
  }

  &.is_error {
    color: #a83f39;
    background: rgba(255, 97, 88, 0.14);
  }
}

.config_json_group {
  ::v-deep .el-textarea__inner {
    font-family: Menlo, Monaco, Consolas, monospace;
    font-size: 11px;
    color: #6b5c4b;
    background: #fffdf8;
  }
}

.actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 22px;
}
</style>
